import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { StripePaymentSession, getStripePaymentSession } from '../../../API/GoMoto';
import { useFullReset, useKioskState } from '../../../context/Kiosk/kiosk';
import { useLocalization } from '../../../context/Localization/localization';
import { useAppointmentStateUnsafe } from '../../../context/Appointment/appointment';
import { PaymentStatus } from './PaymentStatus';
import { Spinner } from '../../../controls/Spinner/Spinner';
import { GMButton } from '../../../controls/Button';
import { GMDialog, GMDialogError } from '../../../controls/Dialog';

interface IProps {
    onGetKeys: () => void;
    onReturn: () => void;
}

export const StripePaymentStatus: React.FC<IProps> = ({ onGetKeys, onReturn }) => {
    const appointment = useAppointmentStateUnsafe();
    const [paymentSession, setPaymentSession] = useState<StripePaymentSession | undefined | null>(undefined);
    const location = useLocation();
    const {
        dealer: { key: dealerKey },
    } = useKioskState();
    const reset = useFullReset(true, false);
    const Localization = useLocalization();
    const noPaymentDue: boolean = appointment?.paymentStatus === 'no payment due' || appointment?.paymentStatus === 'marked paid';
    const sessionId = useMemo(() => new URLSearchParams(window.location.search)
        .get('session_id'), []);
    const [fetchError, setFetchError] = useState<boolean>(false);
    const [paymentError, setPaymentError] = useState<boolean>(false);

    useEffect(() => {
        if (!appointment) {
            reset();
        }
    }, [appointment, reset]);

    useEffect(() => {
        if (!appointment || !sessionId || noPaymentDue) {
            setPaymentSession(null);
        }
        else if (paymentSession === undefined) {
            getStripePaymentSession(sessionId, dealerKey)
                .then((session: StripePaymentSession) => {
                    if (session.status !== 'complete') {
                        if (session.paymentDetails?.errorMessage) {
                            setPaymentError(true);
                        }
                        else {
                            onReturn();
                        }
                    }
                    else {
                        setPaymentSession(session);
                    }
                })
                .catch(() => setFetchError(true));
        }
    }, [appointment, paymentSession, dealerKey, location, onReturn, noPaymentDue, sessionId]);

    return (
        <>
            {!appointment || paymentSession === undefined ?
                <Spinner /> :
                <PaymentStatus
                    paymentDetails={paymentSession?.paymentDetails}
                    paymentComplete={paymentSession?.status === 'complete'}
                    onGetKeys={onGetKeys}
                    paymentIdLabel={Localization.PaymentId}
                />
            }
            <GMDialog
                ariaLabel={Localization.OhNoPaymentCouldNotBeProcessed}
                open={paymentError}
                onClose={onReturn}
                actions={[
                    <GMButton
                        id={Localization.ClickToRetryPayment}
                        title={Localization.ClickToRetryPayment}
                        ariaLabel={Localization.ClickToRetryPayment}
                        onClick={onReturn}
                    >
                        {Localization.TryAgain}
                    </GMButton>,
                ]}
            >
                {Localization.OhNoPaymentCouldNotBeProcessed}
            </GMDialog>
            <GMDialogError
                ariaLabel={Localization.FailedToGetPaymentStatus}
                open={fetchError}
                onClose={reset}
            >
                {Localization.FailedToGetPaymentStatus}
            </GMDialogError>
        </>
    );
};
